import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Color from 'src/app/model/color';
import HeightAndWidthAware from 'src/app/model/helper-interfaces/height-and-width-aware';
import { SignSizeMaterialDelta } from 'src/app/model/helper-interfaces/sign-size-material-delta';
import Material from 'src/app/model/material/material';
import Board, {
  MaterialChoices,
  MaterialChoicesArray,
} from 'src/app/model/product/board/board';
import BoardPart, {
  BoardPartHeightsAndWidth,
  BoardTypeChoiceArray,
  DEFAULT_DISTANCE_TO_BOTTOM,
  PAINTED_POSTS_COLOR_CHOICE_ARRAY,
  PlacementTypeChoiceArray,
} from 'src/app/model/product/board/board-part';
import BoardType from 'src/app/model/product/board/board-type';
import DistanceToBottomOption, {
  compareDistanceToBottomOptions,
  DISTANCE_TO_BOTTOM_OPTIONS,
  DISTANCE_TO_BOTTOM_OPTIONS_ARRAY,
} from 'src/app/model/product/board/distance-to-bottom';
import PlacementType from 'src/app/model/product/board/placement-type';
import { SignSize } from 'src/app/model/product/board/sign-size';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { RequestService } from 'src/app/services/request.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { INT_MAX_SIGNED_32_BIT_VALUE } from 'src/globals/globals';
import { BoardPartFormHelper } from './board-part-form-helper';
import { SignSizeMaterialDeltaHelper } from './sign-size-material-delta-helper';

@Component({
  selector: 'board-part-form',
  templateUrl: './board-part-form.component.html',
  styleUrls: ['./board-part-form.component.scss'],
})
export class BoardPartFormComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  materialOptions: Material[] = MaterialChoicesArray;
  boardTypeOptions: BoardType[] = BoardTypeChoiceArray;
  placementTypeOptions: PlacementType[] = PlacementTypeChoiceArray;
  distanceToBottomOptions: DistanceToBottomOption[] =
    DISTANCE_TO_BOTTOM_OPTIONS_ARRAY;
  paintedPostsColorOptions: Color[] = PAINTED_POSTS_COLOR_CHOICE_ARRAY;
  INT_MAX_SIGNED_32_BIT_VALUE = INT_MAX_SIGNED_32_BIT_VALUE;

  @Input()
  board: Board;
  @Input()
  boardPart: BoardPart;
  @Input()
  index: number;
  @Input()
  client: boolean = false;
  @Output()
  update = new EventEmitter<any>();
  @Output()
  delete = new EventEmitter<any>();

  unselectMeasurements = new EventEmitter<any>();

  heightAndWidthOptions = BoardPartHeightsAndWidth;
  alternativeHeight: number;
  alternativeWidth: number;
  defaultDimensions: boolean = true;

  private matchSignSizeTrigger = new Subject<any>();
  private matchSignSizeSubscription: Subscription;

  selectedDistanceToBottomOption: DistanceToBottomOption;
  showDistanceToBottomOption: boolean;

  constructor(
    private requestService: RequestService,
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeSelectedDistanceToBottomOption();
    if (this.boardPart.id != null) {
      this.initializeAlternativeDimensions();
    }
    this.matchSignSizeSubscription = this.matchSignSizeTrigger
      .pipe(
        startWith(this.boardPart.signSize),
        switchMap(() => this.fetchSignSize())
      )
      .subscribe((signSize: SignSize) => {
        this.handleSignSizeChange(signSize);
      });
  }

  ngOnDestroy(): void {
    if (this.matchSignSizeSubscription != null) {
      this.matchSignSizeSubscription.unsubscribe();
    }
    this.matchSignSizeTrigger.complete();
    this.unselectMeasurements.complete();
  }

  materialUpdate(event: Material): void {
    this.boardPart.material = event;
    this.projectDisplayObservableService
      .getProductMaterialChangedSubscribable()
      .next();
    this.matchSignSizeTrigger.next();
    this.update.emit();
  }

  otherMaterialChange(event: string): void {
    this.boardPart.otherMaterial = event;
    this.projectDisplayObservableService
      .getProductMaterialChangedSubscribable()
      .next();
    this.update.emit();
  }

  defaultDimensionsChanged(value: boolean): void {
    this.defaultDimensions = value;
    this.boardPart.width = null;
    this.boardPart.height = null;
    if (value) {
      this.alternativeWidth = null;
      this.alternativeHeight = null;
    }
    this.projectDisplayObservableService
      .getProductSizeChangedSubscribable()
      .next();
    this.matchSignSizeTrigger.next();
  }

  setHeightAndWidth(event: HeightAndWidthAware): void {
    this.alternativeHeight = null;
    this.alternativeWidth = null;
    this.boardPart.height = event.height;
    this.boardPart.width = event.width;
    this.projectDisplayObservableService
      .getProductSizeChangedSubscribable()
      .next();
    this.matchSignSizeTrigger.next();
    this.update.emit();
  }

  heightChange(event: number): void {
    this.alternativeHeight = event;
    this.boardPart.height = event;
    this.projectDisplayObservableService
      .getProductSizeChangedSubscribable()
      .next();
    this.matchSignSizeTrigger.next();
    this.unselectMeasurements.emit();
    this.update.emit();
  }

  widthChange(event: number): void {
    this.alternativeWidth = event;
    this.boardPart.width = event;
    this.projectDisplayObservableService
      .getProductSizeChangedSubscribable()
      .next();
    this.matchSignSizeTrigger.next();
    this.unselectMeasurements.emit();
    this.update.emit();
  }

  setBoardType(event: BoardType): void {
    this.boardPart.boardType = event;
    this.projectDisplayObservableService
      .getProductPartTypeChangedSubscribable()
      .next();
    this.matchSignSizeTrigger.next();
    this.update.emit();
  }

  setPlacementType(event: PlacementType): void {
    this.boardPart.placementType = event;
    this.handleAutoNoDistanceToBottom();
    this.projectDisplayObservableService
      .getProductPlacementVariableChangedSubscribable()
      .next();
    this.matchSignSizeTrigger.next();
    this.update.emit();
  }

  private handleAutoNoDistanceToBottom(): void {
    const previousShowDistanceToBottomOption = this.showDistanceToBottomOption;
    this.showDistanceToBottomOption =
      BoardPartFormHelper.calculateShowDistanceToBottomSelector(this.boardPart);
    if (
      !this.showDistanceToBottomOption &&
      previousShowDistanceToBottomOption
    ) {
      this.boardPart.distanceToBottom = 0;
      this.selectedDistanceToBottomOption = DISTANCE_TO_BOTTOM_OPTIONS.CUSTOM;
      this.boardPart.distanceToBottomText = null;
    } else if (
      this.showDistanceToBottomOption &&
      !previousShowDistanceToBottomOption
    ) {
      this.boardPart.distanceToBottom = DEFAULT_DISTANCE_TO_BOTTOM;
      this.selectedDistanceToBottomOption = DISTANCE_TO_BOTTOM_OPTIONS._160CM;
    }
  }

  otherPlacementTypeChange(): void {
    this.projectDisplayObservableService
      .getProductPlacementVariableChangedSubscribable()
      .next();
    this.update.emit();
  }

  setSelectedDistanceToBottomOption(
    distanceToBottomOption: DistanceToBottomOption
  ) {
    this.boardPart.distanceToBottom = distanceToBottomOption.value;
    this.matchSignSizeTrigger.next();
    this.update.emit();
  }

  distanceToBottomTextChange(value: string): void {
    this.boardPart.distanceToBottomText = value;
    this.update.emit();
  }

  paintedPostsChange(value: boolean): void {
    this.boardPart.paintedPosts = value;
    this.projectDisplayObservableService
      .getProductPlacementVariableChangedSubscribable()
      .next();
    this.projectDisplayObservableService
      .getPaintedPostsChangedSubscribable()
      .next();
    this.update.emit();
  }

  paintedPostsValueChange(value: Color): void {
    this.boardPart.paintedPostsValue = value;
    this.update.emit();
  }

  paintedPostsTextChange(): void {
    this.update.emit();
  }

  triggerSignSizeMatcher(): void {
    this.matchSignSizeTrigger.next();
  }

  private fetchSignSize(): Observable<SignSize> {
    if (
      this.boardPart.boardType == null ||
      this.boardPart.placementType == null ||
      this.boardPart.material == null ||
      this.boardPart.material.ordinal === MaterialChoices.UNSPECIFIED.ordinal ||
      this.boardPart.width == null ||
      this.boardPart.height == null ||
      this.boardPart.distanceToBottom == null
    ) {
      return of(null);
    } else {
      return this.requestService.post(
        this.constructMatchSignSizeRequest(this.boardPart),
        '/sign_sizes/matchSignSize'
      );
    }
  }

  private constructMatchSignSizeRequest(
    boardPart: BoardPart
  ): MatchSignSizeRequest {
    return {
      boardType: boardPart.boardType,
      placementType: boardPart.placementType,
      material: boardPart.material,
      width: boardPart.width,
      height: boardPart.height,
      cmToBottom: boardPart.distanceToBottom,
    };
  }

  private initializeAlternativeDimensions(): void {
    if (
      (this.boardPart.height != null || this.boardPart.width != null) &&
      !CollectionHelper.some(
        this.heightAndWidthOptions,
        (o) =>
          this.boardPart.height === o.height && this.boardPart.width === o.width
      )
    ) {
      this.defaultDimensions = false;
      this.alternativeHeight = this.boardPart.height;
      this.alternativeWidth = this.boardPart.width;
    }
  }

  private initializeSelectedDistanceToBottomOption(): void {
    this.showDistanceToBottomOption =
      BoardPartFormHelper.calculateShowDistanceToBottomSelector(this.boardPart);
    this.selectedDistanceToBottomOption = DISTANCE_TO_BOTTOM_OPTIONS_ARRAY.find(
      (o) => o.value == this.boardPart.distanceToBottom
    );
  }

  private handleSignSizeChange(signSize: SignSize): void {
    if (
      (this.boardPart.signSize == null && signSize == null) ||
      (this.boardPart.signSize != null &&
        signSize != null &&
        this.boardPart.signSize.id === signSize.id)
    ) {
      return;
    }
    const previousSize = this.boardPart.signSize;
    this.boardPart.signSize = signSize;
    this.projectDisplayObservableService
      .getProductSizeChangedSubscribable()
      .next(
        !this.client && this.boardPart.id != null
          ? {
              productId: this.board.id,
              partId: this.boardPart.id,
            }
          : null
      );
    this.projectDisplayObservableService
      .getProductSizeChangedNoSideEffectSubscribable()
      .next();
    if (!this.client) {
      this.handleCalculateMaterialDelta(
        previousSize == null ? null : previousSize.id,
        this.boardPart.signSize == null ? null : this.boardPart.signSize.id
      );
    }
  }

  private handleCalculateMaterialDelta(
    previousSizeId: number,
    newSizeId: number
  ): void {
    this.addSubscription(
      this.requestService.getIndividual(
        '/sign_sizes/getSignSizeMaterialDelta',
        {
          newSizeId: newSizeId == null ? 0 : newSizeId,
          previousSizeId: previousSizeId == null ? 0 : previousSizeId,
        }
      ),
      (response: any) => {
        if (response != null && response.body != null) {
          const result = SignSizeMaterialDeltaHelper.processMaterialDelta(
            this.board,
            this.boardPart,
            previousSizeId,
            newSizeId,
            <SignSizeMaterialDelta>response.body
          );
          this.update.emit();
          if (result.assetsChanged) {
            this.projectDisplayObservableService
              .getProductAssetsChangedSubscribable()
              .next({ productId: this.board.id, formSection: 'sketchUp' });
          }
        }
      }
    );
  }

  ordinalEnumGetter(option: any): string {
    return GeneralHelper.ordinalEnumGetter(option);
  }

  compareMaterial(o1: any, o2: any): boolean {
    return GeneralHelper.compareMaterial(o1, o2);
  }

  compareOrdinalEnum(o1: any, o2: any): boolean {
    return GeneralHelper.compareOrdinalEnum(o1, o2);
  }

  compareDistanceToBottomOptions: (
    o1: DistanceToBottomOption,
    o2: DistanceToBottomOption
  ) => boolean = compareDistanceToBottomOptions;
}

interface MatchSignSizeRequest {
  boardType: BoardType;
  placementType: PlacementType;
  material: Material;
  width: number;
  height: number;
  cmToBottom: number;
}
